import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Megaphone, Network, PhoneCall, ClipboardList, X,
  TrendingUp, Users, Target, BarChart3, CheckCircle, ArrowRight,
  ArrowUpRight, Layers, Award
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Shared primitives ─── */
const ProgressBar = ({ pct, color }) => (
  <div className="w-full h-1.5 bg-stone-200 rounded-full overflow-hidden">
    <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${Math.min(pct, 100)}%` }} />
  </div>
);

const InlineStatRow = ({ label, count, pct, color }) => (
  <div className="mb-2">
    <div className="flex justify-between items-center text-[11px] font-medium mb-0.5">
      <span className="text-stone-600">{label}</span>
      <span className="font-bold text-stone-800">{(count || 0).toLocaleString()} <span className="text-stone-400 font-normal">· {pct}%</span></span>
    </div>
    <ProgressBar pct={pct} color={color} />
  </div>
);

const DailyReport = ({ ri }) => ri ? (
  <div className="mt-3 pt-3 border-t border-stone-100">
    <div className="flex justify-between text-xs font-semibold text-stone-600 mb-1">
      <span className="flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Daily Work Report</span>
      <span className={ri.donePct >= 80 ? 'text-emerald-600' : ri.donePct >= 50 ? 'text-amber-600' : 'text-red-500'}>
        {ri.done}/{ri.total} · {ri.donePct}%
      </span>
    </div>
    <ProgressBar pct={ri.donePct} color={ri.donePct >= 80 ? 'bg-emerald-500' : ri.donePct >= 50 ? 'bg-amber-400' : 'bg-red-400'} />
  </div>
) : null;

const STATUS_COLORS_BG = { INPROCESS: 'bg-amber-400', DONE: 'bg-emerald-500', HOLD: 'bg-sky-400', CANCEL: 'bg-rose-500' };
const STATUS_BORDER_CARD = { INPROCESS: 'border-amber-200 bg-amber-50', DONE: 'border-emerald-200 bg-emerald-50', HOLD: 'border-sky-200 bg-sky-50', CANCEL: 'border-rose-200 bg-rose-50' };
const STATUS_TEXT = { INPROCESS: 'text-amber-700', DONE: 'text-emerald-700', HOLD: 'text-sky-700', CANCEL: 'text-rose-700' };

/* ─── Detail Modal ─── */
const DetailModal = ({ person, data, onClose }) => {
  if (!data) return null;
  const { assistant, manager, digital } = data;

  const renderContent = () => {
    if (person === 'assistant' && assistant) {
      const statusEntries = Object.entries(assistant.status || {}).sort((a, b) => b[1].count - a[1].count);
      return (
        <div className="space-y-5" data-testid="modal-assistant-content">
          <div className="grid grid-cols-2 gap-3">
            <div className="p-4 rounded-xl bg-fuchsia-50 border border-fuchsia-200 text-center">
              <p className="text-2xl font-bold text-fuchsia-700">{(assistant.totalLeads || 0).toLocaleString()}</p>
              <p className="text-xs text-fuchsia-500 mt-0.5">Total Leads</p>
            </div>
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-center">
              <p className="text-2xl font-bold text-emerald-700">{(assistant.status?.DONE?.count || 0).toLocaleString()}</p>
              <p className="text-xs text-emerald-500 mt-0.5">Done</p>
            </div>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-3">Lead Status Breakdown</p>
            <div className="space-y-2">
              {statusEntries.map(([st, v]) => (
                <div key={st} className={`p-3 rounded-lg border ${STATUS_BORDER_CARD[st] || 'border-stone-200 bg-stone-50'}`}>
                  <div className="flex justify-between items-center mb-1">
                    <span className={`text-sm font-bold ${STATUS_TEXT[st] || 'text-stone-700'}`}>{st}</span>
                    <span className="text-sm font-bold text-stone-700">{(v.count || 0).toLocaleString()} <span className="text-stone-400 font-normal text-xs">· {v.pct}%</span></span>
                  </div>
                  <ProgressBar pct={v.pct} color={STATUS_COLORS_BG[st] || 'bg-stone-400'} />
                </div>
              ))}
            </div>
          </div>
          <DailyReport ri={assistant.reportIndex} />
        </div>
      );
    }

    if (person === 'manager' && manager) {
      return (
        <div className="space-y-5" data-testid="modal-manager-content">
          <div className="grid grid-cols-3 gap-3">
            <div className="p-4 rounded-xl bg-blue-50 border border-blue-200 text-center">
              <p className="text-2xl font-bold text-blue-700">{manager.totalDMs || 0}</p>
              <p className="text-xs text-blue-500 mt-0.5">DMs</p>
            </div>
            <div className="p-4 rounded-xl bg-indigo-50 border border-indigo-200 text-center">
              <p className="text-2xl font-bold text-indigo-700">{manager.totalADMs || 0}</p>
              <p className="text-xs text-indigo-500 mt-0.5">ADMs</p>
            </div>
            <div className="p-4 rounded-xl bg-violet-50 border border-violet-200 text-center">
              <p className="text-2xl font-bold text-violet-700">{manager.totalCPs || 0}</p>
              <p className="text-xs text-violet-500 mt-0.5">CPs</p>
            </div>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-3">CP Network — {(manager.totalRecords || 0).toLocaleString()} Records</p>
            <div className="space-y-2">
              {[
                { label: 'Active CPs',  data: manager.active,   color: 'bg-emerald-500', tid: 'modal-cp-active' },
                { label: 'Top 1000',    data: manager.top1000,  color: 'bg-indigo-500',  tid: 'modal-cp-top1000' },
                { label: 'Kit Paid',    data: manager.kitPaid,  color: 'bg-violet-500',  tid: 'modal-cp-kit' },
                { label: 'Bookings',    data: manager.bookings, color: 'bg-teal-500',    tid: 'modal-cp-bookings' },
              ].map(({ label, data: d, color, tid }) => (
                <div key={label} className="p-3 rounded-lg bg-stone-50 border border-stone-200" data-testid={tid}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-semibold text-stone-700">{label}</span>
                    <span className="text-sm font-bold text-stone-800">{(d?.count || 0).toLocaleString()} <span className="text-stone-400 font-normal text-xs">· {d?.pct || 0}%</span></span>
                  </div>
                  <ProgressBar pct={d?.pct || 0} color={color} />
                </div>
              ))}
            </div>
          </div>
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
            <p className="text-xs font-bold text-amber-700 mb-2">CP Income Tiers</p>
            <div className="flex gap-4 text-sm">
              <span className="font-semibold text-amber-700">₹1L+ <span className="font-normal text-amber-600">{manager.income1lakh}</span></span>
              <span className="font-semibold text-amber-700">₹5L+ <span className="font-normal text-amber-600">{manager.income5lakh}</span></span>
              <span className="font-semibold text-amber-700">₹1Cr+ <span className="font-normal text-amber-600">{manager.income1cr}</span></span>
            </div>
          </div>
          <DailyReport ri={manager.reportIndex} />
        </div>
      );
    }

    if (person === 'digital' && digital) {
      return (
        <div className="space-y-5" data-testid="modal-digital-content">
          <div className="grid grid-cols-2 gap-3">
            <div className="p-4 rounded-xl bg-cyan-50 border border-cyan-200 text-center">
              <p className="text-2xl font-bold text-cyan-700">{(digital.totalLeads || 0).toLocaleString()}</p>
              <p className="text-xs text-cyan-500 mt-0.5">Total Leads</p>
            </div>
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-center">
              <p className="text-2xl font-bold text-emerald-700">{(digital.booking?.count || 0).toLocaleString()}</p>
              <p className="text-xs text-emerald-500 mt-0.5">Bookings</p>
            </div>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-3">Full Conversion Funnel</p>
            <div className="space-y-2">
              {[
                { label: 'Office Visit',      data: digital.officeVisit,     color: 'bg-cyan-500',    tid: 'modal-dm-office' },
                { label: 'Project Visit',     data: digital.projectVisit,    color: 'bg-sky-500',     tid: 'modal-dm-project' },
                { label: 'Scope of Customer', data: digital.scopeOfCustomer, color: 'bg-blue-500',    tid: 'modal-dm-scope' },
                { label: 'Booking',           data: digital.booking,         color: 'bg-emerald-500', tid: 'modal-dm-booking' },
              ].map(({ label, data: d, color, tid }) => (
                <div key={label} className="p-3 rounded-lg bg-stone-50 border border-stone-200" data-testid={tid}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-semibold text-stone-700">{label}</span>
                    <span className="text-sm font-bold text-stone-800">{(d?.count || 0).toLocaleString()} <span className="text-stone-400 font-normal text-xs">· {d?.pct || 0}%</span></span>
                  </div>
                  <ProgressBar pct={d?.pct || 0} color={color} />
                </div>
              ))}
            </div>
          </div>
          <DailyReport ri={digital.reportIndex} />
        </div>
      );
    }
    return null;
  };

  const meta = {
    assistant: { name: assistant?.name || 'Chaitrali Ingale', role: 'Assistant Marketing Manager', gradient: 'from-fuchsia-600 to-pink-600', icon: Megaphone },
    manager:   { name: manager?.name || 'Tushar Dhamal',      role: 'Marketing Manager · CP Tracker', gradient: 'from-indigo-600 to-blue-600', icon: Network },
    digital:   { name: digital?.name || 'Komal Markad',       role: digital?.role || 'Telecalling & Digital Marketing', gradient: 'from-cyan-600 to-sky-600', icon: PhoneCall },
  }[person] || {};
  const Icon = meta.icon || Megaphone;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid={`modal-${person}`}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[88vh] overflow-hidden">
        <div className={`bg-gradient-to-r ${meta.gradient} px-5 py-4 flex items-center justify-between flex-shrink-0`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white font-bold text-sm">{meta.name}</p>
              <p className="text-white/70 text-xs">{meta.role}</p>
            </div>
          </div>
          <button onClick={onClose} data-testid={`modal-${person}-close`} className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

/* ─── Enhanced Profile Card ─── */
const ProfileCard = ({ person, name, role, gradient, Icon, accent, onClick, testid, inlineRows, footerStats }) => (
  <button onClick={onClick} data-testid={testid} className="group text-left w-full focus:outline-none">
    <Card className="border border-stone-200 shadow-sm hover:shadow-lg transition-all duration-200 group-hover:-translate-y-0.5 overflow-hidden h-full">
      {/* Gradient header strip */}
      <div className={`bg-gradient-to-r ${gradient} p-4`}>
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0 shadow">
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-bold text-white text-sm leading-tight">{name}</p>
            <p className="text-white/75 text-[11px] mt-0.5 leading-tight">{role}</p>
          </div>
          <ArrowUpRight className="w-4 h-4 text-white/50 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0" />
        </div>
      </div>

      <CardContent className="p-4 space-y-3">
        {/* Footer stat tiles */}
        {footerStats && (
          <div className="grid grid-cols-3 gap-2">
            {footerStats.map((s, i) => (
              <div key={i} className={`p-2 rounded-xl ${s.bg} border ${s.border} text-center`}>
                <p className={`text-base font-bold ${s.valueColor} leading-tight`}>{s.value}</p>
                <p className={`text-[10px] ${s.labelColor} font-medium leading-tight mt-0.5`}>{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Inline status/funnel rows */}
        {inlineRows && inlineRows.length > 0 && (
          <div className="space-y-1.5">
            {inlineRows.map((row, i) => (
              <InlineStatRow key={i} label={row.label} count={row.count} pct={row.pct} color={row.color} />
            ))}
          </div>
        )}

        {/* CTA */}
        <div className={`flex items-center justify-between px-3 py-2 rounded-xl bg-gradient-to-r ${gradient} text-white text-xs font-semibold group-hover:opacity-95 transition-opacity`}>
          <span>View Full Report</span>
          <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
        </div>
      </CardContent>
    </Card>
  </button>
);

/* ─── Main export ─── */
export const CMDMarketingPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(null);

  useEffect(() => {
    axios.get(`${API}/marketing-dashboard/cmd-overview`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="py-8 text-center text-stone-400 text-sm" data-testid="cmd-marketing-loading">
      <div className="animate-spin w-6 h-6 rounded-full border-b-2 border-fuchsia-500 mx-auto mb-2" />
      Loading Marketing summary…
    </div>
  );
  if (!data) return null;

  const { assistant, manager, digital } = data;

  const assistantTotal = assistant?.totalLeads || 0;
  const digitalTotal = digital?.totalLeads || 0;

  const cards = [
    {
      id: 'assistant',
      name: assistant?.name || 'Chaitrali Ingale',
      role: 'Assistant Marketing Manager',
      gradient: 'from-fuchsia-500 to-pink-500',
      Icon: Megaphone,
      accent: 'text-fuchsia-600',
      testid: 'cmd-card-assistant',
      footerStats: [
        { label: 'Total Leads', value: assistantTotal.toLocaleString(), bg: 'bg-fuchsia-50', border: 'border-fuchsia-200', valueColor: 'text-fuchsia-700', labelColor: 'text-fuchsia-500' },
        { label: 'Done',        value: (assistant?.status?.DONE?.count || 0).toLocaleString(), bg: 'bg-emerald-50', border: 'border-emerald-200', valueColor: 'text-emerald-700', labelColor: 'text-emerald-500' },
        { label: 'Work',        value: `${assistant?.reportIndex?.donePct || 0}%`, bg: 'bg-stone-50', border: 'border-stone-200', valueColor: 'text-stone-700', labelColor: 'text-stone-400' },
      ],
      inlineRows: [
        { label: 'INPROCESS', count: assistant?.status?.INPROCESS?.count || 0, pct: assistant?.status?.INPROCESS?.pct || 0, color: 'bg-amber-400' },
        { label: 'DONE',      count: assistant?.status?.DONE?.count || 0,      pct: assistant?.status?.DONE?.pct || 0,      color: 'bg-emerald-500' },
        { label: 'HOLD',      count: assistant?.status?.HOLD?.count || 0,      pct: assistant?.status?.HOLD?.pct || 0,      color: 'bg-sky-400' },
        { label: 'CANCEL',    count: assistant?.status?.CANCEL?.count || 0,    pct: assistant?.status?.CANCEL?.pct || 0,    color: 'bg-rose-500' },
      ],
    },
    {
      id: 'manager',
      name: manager?.name || 'Tushar Dhamal',
      role: 'Marketing Manager · CP Tracker',
      gradient: 'from-indigo-500 to-blue-500',
      Icon: Network,
      accent: 'text-indigo-600',
      testid: 'cmd-card-manager',
      footerStats: [
        { label: 'DMs',  value: manager?.totalDMs  || 0, bg: 'bg-blue-50',   border: 'border-blue-200',   valueColor: 'text-blue-700',   labelColor: 'text-blue-500' },
        { label: 'ADMs', value: manager?.totalADMs || 0, bg: 'bg-indigo-50', border: 'border-indigo-200', valueColor: 'text-indigo-700', labelColor: 'text-indigo-500' },
        { label: 'CPs',  value: (manager?.totalCPs || 0).toLocaleString(), bg: 'bg-violet-50', border: 'border-violet-200', valueColor: 'text-violet-700', labelColor: 'text-violet-500' },
      ],
      inlineRows: [
        { label: 'Active CPs', count: manager?.active?.count || 0,   pct: manager?.active?.pct || 0,   color: 'bg-emerald-500' },
        { label: 'Top 1000',   count: manager?.top1000?.count || 0,  pct: manager?.top1000?.pct || 0,  color: 'bg-indigo-500' },
        { label: 'Kit Paid',   count: manager?.kitPaid?.count || 0,  pct: manager?.kitPaid?.pct || 0,  color: 'bg-violet-500' },
        { label: 'Bookings',   count: manager?.bookings?.count || 0, pct: manager?.bookings?.pct || 0, color: 'bg-teal-500' },
      ],
    },
    {
      id: 'digital',
      name: digital?.name || 'Komal Markad',
      role: 'Telecalling & Digital Marketing',
      gradient: 'from-cyan-500 to-sky-500',
      Icon: PhoneCall,
      accent: 'text-cyan-600',
      testid: 'cmd-card-digital',
      footerStats: [
        { label: 'Leads',    value: digitalTotal.toLocaleString(), bg: 'bg-cyan-50', border: 'border-cyan-200', valueColor: 'text-cyan-700', labelColor: 'text-cyan-500' },
        { label: 'Office',   value: `${digital?.officeVisit?.pct || 0}%`, bg: 'bg-sky-50', border: 'border-sky-200', valueColor: 'text-sky-700', labelColor: 'text-sky-500' },
        { label: 'Booked',   value: (digital?.booking?.count || 0).toLocaleString(), bg: 'bg-emerald-50', border: 'border-emerald-200', valueColor: 'text-emerald-700', labelColor: 'text-emerald-500' },
      ],
      inlineRows: [
        { label: 'Office Visit',   count: digital?.officeVisit?.count || 0,   pct: digital?.officeVisit?.pct || 0,   color: 'bg-cyan-500' },
        { label: 'Project Visit',  count: digital?.projectVisit?.count || 0,  pct: digital?.projectVisit?.pct || 0,  color: 'bg-sky-500' },
        { label: 'Scope',          count: digital?.scopeOfCustomer?.count || 0, pct: digital?.scopeOfCustomer?.pct || 0, color: 'bg-blue-500' },
        { label: 'Booking',        count: digital?.booking?.count || 0,        pct: digital?.booking?.pct || 0,        color: 'bg-emerald-500' },
      ],
    },
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-5 pt-2" data-testid="cmd-marketing-section">
        <div>
          <h2 className="text-base font-bold text-stone-800 flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-fuchsia-600" /> Marketing Team
          </h2>
          <p className="text-xs text-stone-400 mt-0.5">Click any card to view the full detailed report</p>
        </div>
        <span className="px-3 py-1 rounded-full bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-600 text-xs font-semibold">3 Managers</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="cmd-marketing-cards">
        {cards.map((card) => (
          <ProfileCard key={card.id} {...card} onClick={() => setOpenModal(card.id)} />
        ))}
      </div>

      {openModal && (
        <DetailModal person={openModal} data={data} onClose={() => setOpenModal(null)} />
      )}
    </>
  );
};

export default CMDMarketingPanel;
